<template>
  <main class="ti" :class="pausado ? 'ti--pausado' : 'ti--inativo'">
    <section class="ti__cartao" role="status" aria-live="polite">
      <div class="ti__faixa" aria-hidden="true" />

      <header class="ti__topo">
        <img v-if="logo" class="ti__logo" :src="logo" alt="" @error="trocarLogo">
      </header>

      <div class="ti__corpo">
        <div class="ti__icone" aria-hidden="true">
          <svg v-if="pausado" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94
              7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor"
            stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </div>

        <span class="ti__selo">{{ textos.selo }}</span>
        <h1 class="ti__titulo">{{ textos.titulo }}</h1>
        <p class="ti__texto">{{ texto }}</p>

        <div v-if="previsao" class="ti__previsao">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          <span>Previsão de retorno</span>
          <strong>{{ previsao }}</strong>
        </div>
      </div>

      <footer class="ti__rodape">
        <p v-if="pausado" class="ti__status">
          <span class="ti__ponto" aria-hidden="true" />
          <span>
            Verificando automaticamente.
            Esta página abre o sistema assim que ele voltar.
            <template v-if="horaUltimaVerificacao">
              <br><small>Última verificação às {{ horaUltimaVerificacao }}</small>
            </template>
          </span>
        </p>

        <button
          type="button"
          class="ti__botao"
          :disabled="estado.verificando"
          @click="verificarAgora"
        >
          <svg class="ti__botao-icone" :class="{ 'ti__botao-icone--girando': estado.verificando }"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v6h-6" />
          </svg>
          {{ estado.verificando ? 'Verificando…' : (pausado ? 'Verificar agora' : 'Tentar novamente') }}
        </button>
      </footer>
    </section>
  </main>
</template>

<script>
import Vue from 'vue';
import {
  estadoTenant, verificar, mensagemPersonalizada, formatarPrevisao, formatarHora,
} from '../services/situacaoTenant';

const LOGO_CLIC = 'https://storage.googleapis.com/download/storage/v1/b/cv-pub-cloud/o/'
  + 'admfw%2Fimagens%2Fidentidade%2Flogo.png?generation=1709291760749147&alt=media';

const TEXTOS = {
  P: {
    selo: 'Manutenção em andamento',
    titulo: 'Estamos em manutenção',
    texto: 'Estamos fazendo melhorias no sistema e o acesso está pausado por alguns instantes. '
      + 'Voltamos em breve.',
  },
  I: {
    selo: 'Ambiente desativado',
    titulo: 'Acesso indisponível',
    texto: 'Este ambiente está desativado no momento. Se você precisa acessá-lo, '
      + 'fale com o administrador da sua empresa ou com o suporte.',
  },
};

export default {
  name: 'TenantIndisponivel',

  data() {
    const subdominio = Vue.prototype.$host && Vue.prototype.$host.subdominio;
    return {
      estado: estadoTenant,
      agora: new Date(),
      relogio: null,
      logo: subdominio
        ? `${process.env.VUE_APP_BUCKET_URL}/${subdominio}/imagens/identidade/logo.png`
        : LOGO_CLIC,
    };
  },

  computed: {
    pausado() {
      return this.estado.situacao === 'P';
    },
    textos() {
      return TEXTOS[this.pausado ? 'P' : 'I'];
    },
    texto() {
      return (this.pausado && mensagemPersonalizada(this.estado.mensagem)) || this.textos.texto;
    },
    previsao() {
      return this.pausado ? formatarPrevisao(this.estado.previsaoRetorno, this.agora) : '';
    },
    horaUltimaVerificacao() {
      const data = this.estado.ultimaVerificacao;
      return data ? formatarHora(data) : '';
    },
  },

  watch: {
    'textos.titulo': function atualizarTitulo(novo) {
      document.title = novo;
    },
  },

  created() {
    this.tituloAnterior = document.title;
    document.title = this.textos.titulo;
    this.relogio = setInterval(() => { this.agora = new Date(); }, 30 * 1000);
  },

  beforeDestroy() {
    clearInterval(this.relogio);
    document.title = this.tituloAnterior;
  },

  methods: {
    verificarAgora() {
      verificar();
    },
    trocarLogo() {
      this.logo = this.logo === LOGO_CLIC ? '' : LOGO_CLIC;
    },
  },
};
</script>

<style scoped>
.ti {
  --ti-marinho: #0d2c4b;
  --ti-texto: #3e4c5e;
  --ti-texto-suave: #6b7a8c;
  --ti-borda: #e6e9ee;
  --ti-fundo: #f3f5f8;
  --ti-cartao: #ffffff;
  --ti-destaque: #e8792b;
  --ti-destaque-escuro: #b4561a;
  --ti-destaque-fundo: #fff1e6;

  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background:
    radial-gradient(circle at 15% 10%, rgba(232, 121, 43, 0.08), transparent 40%),
    radial-gradient(circle at 85% 90%, rgba(13, 44, 75, 0.08), transparent 45%),
    var(--ti-fundo);
  text-align: center;
}

.ti--inativo {
  --ti-destaque: #64748b;
  --ti-destaque-escuro: #475569;
  --ti-destaque-fundo: #eef1f5;
}

.ti *,
.ti *::before,
.ti *::after {
  box-sizing: border-box;
}

.ti .ti__cartao {
  position: relative;
  width: 100%;
  max-width: 460px;
  overflow: hidden;
  border: 1px solid var(--ti-borda);
  border-radius: 20px;
  background: var(--ti-cartao);
  box-shadow: 0 1px 2px rgba(13, 44, 75, 0.04), 0 20px 48px rgba(13, 44, 75, 0.1);
  animation: ti-entrar 0.4s ease-out both;
}

.ti .ti__faixa {
  height: 4px;
  background: linear-gradient(90deg, var(--ti-destaque), var(--ti-marinho));
}

.ti .ti__topo {
  padding: 28px 32px 22px;
  border-bottom: 1px solid var(--ti-borda);
}

.ti .ti__logo {
  display: block;
  max-width: 150px;
  max-height: 44px;
  margin: 0 auto;
  object-fit: contain;
}

.ti .ti__corpo {
  padding: 32px 36px 8px;
}

.ti .ti__icone {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto 18px;
  padding: 15px;
  border-radius: 18px;
  background: var(--ti-destaque-fundo);
  color: var(--ti-destaque-escuro);
}

.ti .ti__icone svg {
  display: block;
  width: 100%;
  height: 100%;
}

.ti--pausado .ti__icone svg {
  animation: ti-balancar 3s ease-in-out infinite;
  transform-origin: 70% 30%;
}

.ti .ti__selo {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ti-destaque-escuro);
}

.ti .ti__titulo {
  margin: 0 0 12px;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--ti-marinho);
  text-align: center;
}

.ti .ti__texto {
  margin: 0 auto;
  max-width: 340px;
  font-size: 1rem;
  line-height: 1.6;
  color: var(--ti-texto);
  text-align: center;
  white-space: pre-line;
}

.ti .ti__previsao {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 22px auto 0;
  padding: 12px 16px;
  border: 1px solid rgba(232, 121, 43, 0.25);
  border-radius: 12px;
  background: var(--ti-destaque-fundo);
  font-size: 0.92rem;
  color: var(--ti-destaque-escuro);
}

.ti .ti__previsao svg {
  width: 18px;
  height: 18px;
  flex: none;
}

.ti .ti__previsao strong {
  color: var(--ti-marinho);
}

.ti .ti__rodape {
  padding: 24px 36px 32px;
}

.ti .ti__status {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  margin: 0 0 20px;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--ti-texto-suave);
  text-align: left;
}

.ti .ti__status small {
  font-size: 0.78rem;
  opacity: 0.85;
}

.ti .ti__ponto {
  position: relative;
  flex: none;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: #22a06b;
}

.ti .ti__ponto::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: inherit;
  animation: ti-pulsar 2s ease-out infinite;
}

.ti .ti__botao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 46px;
  padding: 0 24px;
  border: 0;
  border-radius: 10px;
  background: var(--ti-marinho);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.ti .ti__botao:hover:not(:disabled) {
  background: #16406b;
}

.ti .ti__botao:active:not(:disabled) {
  transform: translateY(1px);
}

.ti .ti__botao:focus-visible {
  outline: 3px solid rgba(232, 121, 43, 0.5);
  outline-offset: 2px;
}

.ti .ti__botao:disabled {
  opacity: 0.75;
  cursor: wait;
}

.ti .ti__botao-icone {
  width: 18px;
  height: 18px;
}

.ti .ti__botao-icone--girando {
  animation: ti-girar 0.9s linear infinite;
}

@keyframes ti-entrar {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
}

@keyframes ti-balancar {
  0%, 70%, 100% { transform: rotate(0); }
  80% { transform: rotate(-14deg); }
  90% { transform: rotate(10deg); }
}

@keyframes ti-pulsar {
  from { transform: scale(1); opacity: 0.6; }
  to { transform: scale(2.8); opacity: 0; }
}

@keyframes ti-girar {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ti *,
  .ti *::after {
    animation: none !important;
  }
}

@media (max-width: 480px) {
  .ti .ti__topo { padding: 22px 20px 18px; }
  .ti .ti__corpo { padding: 28px 22px 4px; }
  .ti .ti__rodape { padding: 22px 22px 26px; }
  .ti .ti__titulo { font-size: 1.4rem; }
}
</style>
